<script setup lang="ts">
import { computed } from 'vue'

import { ParagraphType } from '@xcel-next/core/enums'

import { usePageContentSharedSetup } from '../composables/use-page-content-shared-setup'
import { usePageContentShared } from '../composables/use-page-content-shared'
import Advertorial from './content-header/Advertorial.vue'
import Author from './content-header/Author.vue'
import Breadcrumb from './content-header/Breadcrumb.vue'
import Headline from './content-header/Headline.vue'
import IntroductionText from './content-header/IntroductionText.vue'
import Roofline from './content-header/Roofline.vue'
import Paragraphs from './Paragraphs.vue'
import { ParagraphTypeVideo } from './paragraph-types'
import Tags from './shared/Tags.vue'
import TrackingPixel from './tracking/TrackingPixel.vue'
import ShowMore from './shared/ShowMore.vue'
import AdaAdSlot from './ad-slots/AdaAdSlot.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedBody, ComposedMetaData, ComposedPage, ComposedPageMeta } from '@xcel-next/composer-schema'
import type { RenderedParagraph } from '../utils/paragraph-schema/paragraphs'


// `metaData.seeding` is injected by the rendering layer, not part of composer-schema's ComposedMetaData
type PageContentMetaData = ComposedMetaData & {
  seeding?: { enabled?: boolean, headlineId?: string, articleId?: string }
}

defineOptions({ name: 'PageContentVideoOnly' })

const props = withDefaults(defineProps<{
  asset?: ComposedBody,
  brand?: Brand,
  pageMeta?: ComposedPageMeta,
  headerData?: NonNullable<ComposedPage['header']>,
  metaData?: PageContentMetaData,
  // eslint-disable-next-line vue/no-unused-properties
  taboolaEnabled?: boolean
}>(), {
  asset: () => ({}),
  pageMeta: () => ({}),
  headerData: () => ({}),
  metaData: () => ({}),
  taboolaEnabled: false
})

const setupDeps = usePageContentSharedSetup(props)
const { isRootUrl, showHeadline, isArticleOrArticleVersion, assetTags } = usePageContentShared(props, setupDeps)

const openingVideoParagraph = computed(() => {
  const paragraphs = (props.asset?.paragraphs ?? []) as RenderedParagraph[]
  return paragraphs.find(p => p?.paragraphType === ParagraphType.video) ?? null
})

const remainingParagraphs = computed(() => {
  let foundVideoIndex = -1

  const filtered = ((props.asset?.paragraphs ?? []) as RenderedParagraph[]).filter((p, i) => {
    if (foundVideoIndex === -1 && p?.paragraphType === ParagraphType.video) {
      foundVideoIndex = i
      return false // erstes Video rauswerfen
    }
    return true
  })

  return filtered.map((p, i) => ({
    ...p,
    listOrderPosition: i
  }))
})
</script>

<template>
  <main class="asset-container bx-grid--container">
    <slot name="content-top" />
    <Breadcrumb v-if="asset.mainStructurePath && !isRootUrl"
                :breadcrumb="asset.mainStructurePath"
                :page-type="pageMeta.pageType" />
    <Advertorial v-if="metaData && metaData.advertorial && !metaData.hideAdvertorial" />
    <TrackingPixel v-if="metaData && metaData.trackingPixelContent"
                   :tracking-pixel="metaData.trackingPixelContent" />
    <TrackingPixel v-if="metaData && metaData.respondiTrackingPixelContent"
                   :tracking-pixel="metaData.respondiTrackingPixelContent" />

    <Roofline v-if="asset.roofline && asset.roofline.length > 0"
              :text="asset.roofline" />
    <Headline v-if="showHeadline"
              :seeding="metaData.seeding"
              :text="asset.headline" />

    <ParagraphTypeVideo v-if="openingVideoParagraph?.video"
                        :video="openingVideoParagraph.video"
                        :list-order-position="openingVideoParagraph.listOrderPosition"
                        :include-depth="0" />

    <Author :composed-authors="[]"
            :settings="{
              brand,
              displayDate: metaData.hideDisplayDate ? false : asset.displayDate
            }" />

    <ShowMore v-if="asset.introductionText && asset.introductionText.length > 0"
              controls-id="video-only-introduction-text"
              content-label="Einleitungstext"
              :button-labels="{
                showMore: 'mehr anzeigen',
                showLess: 'weniger anzeigen'
              }"
              :max-lines="2">
      <IntroductionText :html="asset.introductionText" />
    </ShowMore>

    <div class="bx-ada bx-ada--top-slots">
      <AdaAdSlot slot-name="mobile"
                 :slot-id="1" />
      <AdaAdSlot slot-name="superbanner"
                 :slot-id="1" />
    </div>

    <section>
      <div>
        <Paragraphs :paragraphs="remainingParagraphs"
                    :meta-data="metaData"
                    :page-meta="pageMeta"
                    :brand="brand"
                    :page-type="pageMeta.pageType"
                    :include-depth="0">
          <!-- eslint-disable vue/valid-v-slot -->
          <template v-for="(_, slot) of $slots"
                    #[slot]="scope">
            <slot :name="slot"
                  v-bind="scope" />
          </template>
          <!-- eslint-enable vue/valid-v-slot -->
        </Paragraphs>

        <Tags v-if="isArticleOrArticleVersion && assetTags.length > 0"
              :tags="assetTags" />
      </div>
    </section>
  </main>
</template>
